import React, { useMemo } from 'react';
import BarrasHorizontais from '../shared/BarrasHorizontais';
import Funil from '../shared/Funil';
import { funilDeProducao, agruparPor, agruparPorPeriodo, rotuloDePeriodo, taxa } from '../../../data/relatorios';
import { Destaque, Indicador, Cartao, Tabela, Vazio, num, pct, BotaoPDF } from './Pecas';
import { gerarRelatorioPDF, descreverFiltros } from '../../../utils/relatorioPDF';

/**
 * Relatório de embriões: o funil da produção.
 *
 * A métrica que lidera é embriões produzidos — o que o laboratório entrega — e
 * as taxas ao redor explicam como se chegou lá.
 */
const PainelEmbrioes = ({ linhas, dimensao, granularidade, contexto = {} }) => {
  const funil = useMemo(() => funilDeProducao(linhas), [linhas]);
  const grupos = useMemo(() => agruparPor(linhas, dimensao), [linhas, dimensao]);
  const porPeriodo = useMemo(
    () => agruparPorPeriodo(linhas, 'service_date', granularidade, ['embrioes', 'viaveis']),
    [linhas, granularidade]);

  const t = useMemo(() => {
    const acha = (etapa) => funil.find(f => f.etapa === etapa) || { valor: 0, taxa: null };
    return {
      viaveis: acha('Viáveis').valor,
      embrioes: acha('Embriões').valor,
      transferidos: acha('Transferidos').valor,
      clivagem: taxa(acha('Clivados').valor, acha('Viáveis').valor),
      blasto: acha('Embriões').taxa,
    };
  }, [funil]);

  const rotuloDim = dimensao === 'bull_name' ? 'touro'
    : dimensao === 'client_name' ? 'cliente' : 'doadora';

  const COLUNAS = [
          { chave: 'dim', rotulo: rotuloDim.charAt(0).toUpperCase() + rotuloDim.slice(1), valor: (l) => l.chave },
          { chave: 'srv', rotulo: 'Serviços', numerica: true, valor: (l) => num(l.servicos) },
          { chave: 'ooc', rotulo: 'Oócitos', numerica: true, valor: (l) => num(l.oocitos) },
          { chave: 'via', rotulo: 'Viáveis', numerica: true, valor: (l) => num(l.viaveis) },
          {
            chave: 'cliv', rotulo: 'Clivagem', numerica: true,
            // Acima de 100% é erro de lançamento, não desempenho: clivar mais
            // que o viável é impossível.
            valor: (l) => `${pct(l.taxaClivagem)}${l.taxaClivagem > 100 ? ' ?' : ''}`,
            titulo: (l) => (l.taxaClivagem > 100 ? 'Acima de 100% — conferir o lançamento' : undefined),
          },
          { chave: 'emb', rotulo: 'Embriões', numerica: true, forte: true, valor: (l) => num(l.embrioes) },
          { chave: 'bla', rotulo: 'Blasto', numerica: true, valor: (l) => pct(l.taxaBlasto) },
          { chave: 'tra', rotulo: 'Transf.', numerica: true, valor: (l) => num(l.transferidos) },
        ];

  // `t` e `grupos` sao os mesmos dos cartoes e do funil acima.
  const imprimir = () => gerarRelatorioPDF({
    titulo: 'Relatório de produção embrionária',
    laboratorio: contexto.laboratorio,
    filtros: descreverFiltros(contexto),
    indicadores: [
      { rotulo: 'Embriões', valor: num(t.embrioes), detalhe: `de ${num(t.viaveis)} viáveis` },
      { rotulo: 'Clivagem', valor: pct(t.clivagem), detalhe: 'dos viáveis' },
      { rotulo: 'Blastocisto', valor: pct(t.blasto), detalhe: 'dos viáveis' },
      { rotulo: 'Transferidos', valor: num(t.transferidos), detalhe: 'embriões' },
    ],
    detalhe: { titulo: `Por ${rotuloDim}`, colunas: COLUNAS, linhas: grupos },
  });

  if (linhas.length === 0) return <Vazio mensagem="Nenhuma produção no período selecionado." />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Destaque rotulo="Embriões produzidos" valor={num(t.embrioes)}
          detalhe={`de ${num(t.viaveis)} oócitos viáveis`} />
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Indicador rotulo="Clivagem" valor={pct(t.clivagem)} detalhe="dos viáveis" />
          <Indicador rotulo="Blastocisto" valor={pct(t.blasto)} detalhe="dos viáveis" />
          <Indicador rotulo="Transferidos" valor={num(t.transferidos)} detalhe="embriões" />
          <Indicador rotulo="Doadoras" valor={num(grupos.length)} detalhe={`${rotuloDim}(s)`} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Cartao titulo="Funil de produção"
          subtitulo="Cada conversão é sobre a etapa anterior — é ali que a perda aparece.">
          <Funil etapas={funil} />
        </Cartao>

        <Cartao titulo={`Embriões por ${rotuloDim}`}
          subtitulo={grupos.length > 12 ? `As 12 maiores de ${grupos.length}.` : undefined}>
          <BarrasHorizontais
            dados={grupos.slice(0, 12).map(g => ({ rotulo: g.chave, valor: g.embrioes, grupo: g }))}
            detalhe={(d) => `${num(d.grupo.viaveis)} viáveis · clivagem ${pct(d.grupo.taxaClivagem)} · blasto ${pct(d.grupo.taxaBlasto)}`}
          />
        </Cartao>
      </div>

      <Cartao titulo={`Produção por ${granularidade === 'mes' ? 'mês' : 'dia'}`}
        subtitulo="Em ordem cronológica.">
        <BarrasHorizontais larguraRotulo="w-16"
          dados={porPeriodo.map(p => ({ rotulo: rotuloDePeriodo(p.chave), valor: p.embrioes, grupo: p }))}
          detalhe={(d) => `${num(d.grupo.embrioes)} embriões de ${num(d.grupo.viaveis)} viáveis`}
        />
      </Cartao>

      <Tabela
        titulo="Detalhamento"
        subtitulo={`${grupos.length} ${rotuloDim}(s) no período`}
        colunas={COLUNAS}
        linhas={grupos}
        acao={<BotaoPDF onClick={imprimir} />}
      />
    </div>
  );
};

export default PainelEmbrioes;
